// Input. Free cursor always (no pointer lock, never requested; contract section 7):
//   keyboard  W / Arrow Up forward, S / Arrow Down back, A / D strafe, Arrow Left / Right turn, Shift run.
//             Works as soon as the player has entered. Space is reserved for play/pause (onKey; default
//             prevented: no scroll, no click on a focused button). Keys are ignored while an input, textarea
//             or select has focus (typing in the panel does not walk); Escape is always reported (onKey).
//             Shift tap (onShiftTap): Shift pressed and released within SHIFT_TAP_MS, no other key or mouse
//             button pressed in between, no movement key held, focus not in an input: toggles the room controls.
//             Holding Shift while walking runs and is not a tap.
//   mouse     press and drag on the view to look (cursor: grabbing while dragging). A press released within
//             CLICK_PX of where it started and within CLICK_MS is a click (onClick). Moves without a button
//             are reported for hover (onHover / onHoverEnd).
//   touch     left half: virtual joystick; right half: drag to look. A short tap anywhere is a click.
// Produces a move vector (strafe, forward in -1..1), a turn rate (-1..1), a run flag and accumulated look
// deltas (radians), consumed once per frame.

/** A press is a click when it moved less than this (px, total path) ... */
export const CLICK_PX = 5;
/** ... and was released within this (ms). */
export const CLICK_MS = 250;
/** Shift released within this (ms) of its press, with nothing else pressed, is a tap (onShiftTap). */
export const SHIFT_TAP_MS = 250;

/** A button inside a panel marked data-native-keys: Space / Enter activate it (not play/pause). */
const isPanelButton = (t: EventTarget | null): boolean => t instanceof HTMLButtonElement && !!t.closest('[data-native-keys]');

const isTyping = (t: EventTarget | null): boolean => {
  if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) return true;
  return !!(t as HTMLElement | null)?.isContentEditable;
};

export class Input {
  moveX = 0;          // +right
  moveY = 0;          // +forward
  turn = 0;           // +left (arrow keys), -1..1
  run = false;
  lookYaw = 0;        // accumulated since last consume, radians (+ = turn left)
  lookPitch = 0;      // + = look up
  enabled = false;
  /** A mouse drag-look is in progress (past the click threshold). */
  dragging = false;

  private keys = new Set<string>();
  /** Time of the Shift press that may still become a tap; -1: none (another key came in between, or held). */
  private shiftT0 = -1;
  private joyId: number | null = null;
  private joyX0 = 0;
  private joyY0 = 0;
  private joyVX = 0;
  private joyVY = 0;
  private lookId: number | null = null;
  private lookX = 0;
  private lookY = 0;
  // mouse drag-look (pointer events)
  private dragId: number | null = null;
  private dragX = 0;
  private dragY = 0;
  private dragDist = 0;
  private dragT0 = 0;
  // touch taps: start position / time / path length per touch identifier
  private taps = new Map<number, { x: number; y: number; t: number; d: number }>();
  private readonly joyRadius = 56;
  private readonly dragSens = 0.0042;
  private readonly touchSens = 0.0058;

  onKey: (code: string) => void = () => {};
  /** Shift was tapped (see the header). */
  onShiftTap: () => void = () => {};
  /** A click or tap on the view (client coordinates). */
  onClick: (x: number, y: number, touch: boolean) => void = () => {};
  /** The mouse moved over the view without a button (client coordinates). */
  onHover: (x: number, y: number) => void = () => {};
  onHoverEnd: () => void = () => {};
  /** A drag-look started (true) or ended (false). */
  onDrag: (dragging: boolean) => void = () => {};
  /** Any input that may move or turn the view (the render loop sleeps while idle and wakes on this). */
  onActivity: () => void = () => {};

  private joyBase: HTMLDivElement;
  private joyKnob: HTMLDivElement;

  constructor(private el: HTMLElement) {
    this.joyBase = document.createElement('div');
    this.joyBase.className = 'joy-base';
    this.joyKnob = document.createElement('div');
    this.joyKnob.className = 'joy-knob';
    this.joyBase.appendChild(this.joyKnob);
    document.body.appendChild(this.joyBase);

    // capture phase on window: works whatever element has focus (a button, the canvas, nothing)
    addEventListener('keydown', this.keydown, true);
    addEventListener('keyup', this.keyup, true);
    addEventListener('blur', this.releaseAll);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.releaseAll(); });
    el.addEventListener('pointerdown', this.pointerdown);
    el.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch') return;
      // WebKit sends pointerleave when pointer capture is released, with the pointer still over the view
      const r = el.getBoundingClientRect();
      if (e.clientX > r.left && e.clientX < r.right && e.clientY > r.top && e.clientY < r.bottom && document.elementFromPoint(e.clientX, e.clientY) === el) return;
      this.onHoverEnd();
    });
    el.addEventListener('pointermove', this.pointermove);
    el.addEventListener('pointerup', this.pointerup);
    el.addEventListener('pointercancel', this.pointerup);
    el.addEventListener('lostpointercapture', () => this.endDrag());
    el.addEventListener('touchstart', this.touchstart, { passive: false });
    el.addEventListener('touchmove', this.touchmove, { passive: false });
    el.addEventListener('touchend', this.touchend, { passive: false });
    el.addEventListener('touchcancel', this.touchend, { passive: false });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** Any movement or turning input held? */
  get moving(): boolean {
    return this.moveX !== 0 || this.moveY !== 0 || this.turn !== 0;
  }

  consumeLook(out: { yaw: number; pitch: number }): void {
    out.yaw = this.lookYaw;
    out.pitch = this.lookPitch;
    this.lookYaw = 0;
    this.lookPitch = 0;
  }

  /** Release every key and end a drag (window blur, tab hidden, Cmd combos): nothing keeps walking. */
  releaseAll = () => {
    this.keys.clear();
    this.shiftT0 = -1;
    this.endDrag();
    this.update();
  };

  private endDrag(): void {
    this.dragId = null;
    if (this.dragging) {
      this.dragging = false;
      document.body.classList.remove('dragging');
      this.onDrag(false);
    }
  }

  private keydown = (e: KeyboardEvent) => {
    const t = e.target;
    const code = keyCode(e);
    const shift = code === 'ShiftLeft' || code === 'ShiftRight';
    // any other key (Shift+Tab, Shift+arrow, a capital letter) cancels a pending Shift tap
    if (!shift) this.shiftT0 = -1;
    else if (!e.repeat) {
      const movingKeys = [...this.keys].some((k) => k !== 'ShiftLeft' && k !== 'ShiftRight');
      this.shiftT0 = this.enabled && !isTyping(t) && !movingKeys && !e.metaKey && !e.ctrlKey && !e.altKey ? performance.now() : -1;
    }
    if (code === 'Escape') {
      if (!e.repeat) this.onKey(code);
      return;
    }
    if (isTyping(t)) return;
    // buttons inside a panel keep their own Space / Enter (activate the focused button)
    if ((code === 'Space' || code === 'Enter') && isPanelButton(t)) return;
    if (!e.repeat) this.onKey(code); // shortcuts fire once per press, not on auto-repeat
    if (!this.enabled) return;
    // Space is play/pause: no page scroll, and no native click on a focused button (the play button keeps
    // focus after a mouse click; without this, Space would toggle twice)
    if (code === 'Space') { e.preventDefault(); return; }
    // Cmd/Ctrl shortcuts belong to the browser; on macOS the keyup of a key pressed with Cmd never arrives
    if (e.metaKey || e.ctrlKey) { this.releaseAll(); return; }
    if (MOVE_KEYS.has(code)) {
      this.keys.add(code);
      this.update();
      this.onActivity();
      e.preventDefault(); // arrows must not scroll or move focus
    }
  };

  private keyup = (e: KeyboardEvent) => {
    const code = keyCode(e);
    if (code === 'Space' && this.enabled && !isTyping(e.target) && !isPanelButton(e.target)) e.preventDefault();
    if (code === 'MetaLeft' || code === 'MetaRight') this.releaseAll();
    if (this.keys.delete(code)) this.update();
    if ((code === 'ShiftLeft' || code === 'ShiftRight') && this.shiftT0 >= 0) {
      const tap = this.enabled && !isTyping(e.target) && performance.now() - this.shiftT0 < SHIFT_TAP_MS;
      this.shiftT0 = -1;
      if (tap) this.onShiftTap();
    }
  };

  private update(): void {
    const k = this.keys;
    let x = 0, y = 0, turn = 0;
    if (k.has('KeyW') || k.has('ArrowUp')) y += 1;
    if (k.has('KeyS') || k.has('ArrowDown')) y -= 1;
    if (k.has('KeyD')) x += 1;
    if (k.has('KeyA')) x -= 1;
    if (k.has('ArrowLeft')) turn += 1;
    if (k.has('ArrowRight')) turn -= 1;
    this.run = k.has('ShiftLeft') || k.has('ShiftRight');
    if (this.joyId !== null) {
      x = this.joyVX;
      y = this.joyVY;
      this.run = Math.hypot(x, y) > 0.97;
    }
    const l = Math.hypot(x, y);
    if (l > 1) { x /= l; y /= l; }
    this.moveX = x;
    this.moveY = y;
    this.turn = turn;
  }

  // ---------------------------------------------------------------- mouse (pointer events)
  private pointerdown = (e: PointerEvent) => {
    this.shiftT0 = -1; // Shift+click is not a tap
    if (!this.enabled || e.pointerType === 'touch' || e.button !== 0) return;
    this.dragId = e.pointerId;
    this.dragX = e.clientX;
    this.dragY = e.clientY;
    this.dragDist = 0;
    this.dragT0 = performance.now();
    try { this.el.setPointerCapture(e.pointerId); } catch { /* old Safari */ }
    this.el.focus({ preventScroll: true });
  };

  private pointermove = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    if (e.pointerId !== this.dragId) {
      if (this.enabled && this.dragId === null) this.onHover(e.clientX, e.clientY);
      return;
    }
    // client coordinates, not movementX: Safari scales movementX differently, and it is 0 in some cases
    const dx = e.clientX - this.dragX, dy = e.clientY - this.dragY;
    this.dragX = e.clientX;
    this.dragY = e.clientY;
    this.dragDist += Math.abs(dx) + Math.abs(dy);
    if (!this.dragging && this.dragDist >= CLICK_PX) {
      this.dragging = true;
      document.body.classList.add('dragging');
      this.onDrag(true);
    }
    this.lookYaw -= dx * this.dragSens;
    this.lookPitch -= dy * this.dragSens;
    this.onActivity();
  };

  private pointerup = (e: PointerEvent) => {
    if (e.pointerId !== this.dragId) return;
    const click = e.type === 'pointerup' && this.dragDist < CLICK_PX && performance.now() - this.dragT0 < CLICK_MS;
    try { this.el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    this.endDrag();
    if (click) this.onClick(e.clientX, e.clientY, false);
    else this.onHover(e.clientX, e.clientY);
  };

  // ---------------------------------------------------------------- touch
  private touchstart = (e: TouchEvent) => {
    if (!this.enabled) return;
    e.preventDefault();
    this.onActivity();
    const r = this.el.getBoundingClientRect(); // the canvas sits inside the page frame, not at 0,0
    const half = r.left + r.width / 2;
    const now = performance.now();
    for (const t of Array.from(e.changedTouches)) {
      this.taps.set(t.identifier, { x: t.clientX, y: t.clientY, t: now, d: 0 });
      if (t.clientX < half && this.joyId === null) {
        this.joyId = t.identifier;
        this.joyX0 = t.clientX;
        this.joyY0 = t.clientY;
        this.joyVX = this.joyVY = 0;
        this.joyBase.style.transform = `translate(${t.clientX - this.joyRadius}px, ${t.clientY - this.joyRadius}px)`;
        this.joyKnob.style.transform = 'translate(0px, 0px)';
        this.joyBase.classList.add('on');
        this.update();
      } else if (this.lookId === null) {
        this.lookId = t.identifier;
        this.lookX = t.clientX;
        this.lookY = t.clientY;
      }
    }
  };

  private touchmove = (e: TouchEvent) => {
    if (!this.enabled) return;
    e.preventDefault();
    this.onActivity();
    for (const t of Array.from(e.changedTouches)) {
      const tap = this.taps.get(t.identifier);
      if (tap) tap.d = Math.max(tap.d, Math.hypot(t.clientX - tap.x, t.clientY - tap.y));
      if (t.identifier === this.joyId) {
        let dx = t.clientX - this.joyX0, dy = t.clientY - this.joyY0;
        const d = Math.hypot(dx, dy), R = this.joyRadius;
        if (d > R) { dx = (dx / d) * R; dy = (dy / d) * R; }
        this.joyKnob.style.transform = `translate(${dx}px, ${dy}px)`;
        // small dead zone, then a soft curve for fine control
        const m = Math.min(1, d / R);
        const k = m < 0.12 ? 0 : Math.pow((m - 0.12) / 0.88, 1.4) / (m || 1);
        this.joyVX = (dx / R) * k;
        this.joyVY = (-dy / R) * k;
        this.update();
      } else if (t.identifier === this.lookId) {
        this.lookYaw -= (t.clientX - this.lookX) * this.touchSens;
        this.lookPitch -= (t.clientY - this.lookY) * this.touchSens;
        this.lookX = t.clientX;
        this.lookY = t.clientY;
      }
    }
  };

  private touchend = (e: TouchEvent) => {
    const now = performance.now();
    for (const t of Array.from(e.changedTouches)) {
      const tap = this.taps.get(t.identifier);
      this.taps.delete(t.identifier);
      if (tap && e.type === 'touchend' && this.enabled && tap.d < CLICK_PX && now - tap.t < CLICK_MS) this.onClick(t.clientX, t.clientY, true);
      if (t.identifier === this.joyId) {
        this.joyId = null;
        this.joyVX = this.joyVY = 0;
        this.joyBase.classList.remove('on');
        this.update();
      } else if (t.identifier === this.lookId) {
        this.lookId = null;
      }
    }
  };
}

const MOVE_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ShiftLeft', 'ShiftRight']);

const KEY_FALLBACK: Record<string, string> = {
  ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown', ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
  Up: 'ArrowUp', Down: 'ArrowDown', Left: 'ArrowLeft', Right: 'ArrowRight', // old key names
  w: 'KeyW', a: 'KeyA', s: 'KeyS', d: 'KeyD', p: 'KeyP', n: 'KeyN', Shift: 'ShiftLeft', '`': 'Backquote', ' ': 'Space', Spacebar: 'Space',
};

/** `event.code`, or a code derived from `event.key` when the browser leaves code empty or "Unidentified". */
function keyCode(e: KeyboardEvent): string {
  if (e.code && e.code !== 'Unidentified') return e.code;
  const k = e.key ?? '';
  return KEY_FALLBACK[k] ?? KEY_FALLBACK[k.toLowerCase()] ?? k;
}
